import { format, differenceInDays } from 'date-fns';
import { nl } from 'date-fns/locale';
import { MapPin, CurrencyEur, Clock, Wrench } from '@phosphor-icons/react';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { FlowIndicator } from './FlowIndicator';
import { VacantUnit } from '../hooks/useHerverhuurPipeline';

interface VacantCardProps {
  unit: VacantUnit;
}

const formatDate = (date: string | null) => {
  if (!date) return null;
  try {
    return format(new Date(date), 'd MMM yyyy', { locale: nl });
  } catch {
    return null;
  }
};

const daysVacant = (since: string | null): number | null => {
  if (!since) return null;
  try {
    return differenceInDays(new Date(), new Date(since));
  } catch {
    return null;
  }
};

export const VacantCard = ({ unit }: VacantCardProps) => {
  const days = daysVacant(unit.vacantSince);
  const formattedDate = formatDate(unit.vacantSince);

  return (
    <Card className="border border-border shadow-sm hover:shadow-md transition-shadow cursor-default">
      <CardContent className="p-3 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-1.5 min-w-0">
            <MapPin className="text-neutral-secondary-mid mt-0.5 flex-shrink-0" size={13} weight="fill" />
            <div className="min-w-0">
              <p className="text-xs font-medium text-neutral-primary leading-tight truncate">
                {unit.buildingName || unit.buildingId}
              </p>
              <p className="text-[11px] text-neutral-secondary-mid">
                {unit.unitLabel} • {unit.floor !== null ? `Verd. ${unit.floor}` : '—'}
              </p>
            </div>
          </div>
          {unit.vacantDueToRenovation && (
            <Badge variant="outline" className="flex-shrink-0 text-[10px] h-5 px-1.5 border-amber-300 text-amber-700 bg-amber-50 gap-1">
              <Wrench size={10} />
              Renovatie
            </Badge>
          )}
        </div>

        {formattedDate && (
          <div className="flex items-center gap-1.5">
            <Clock className="text-neutral-secondary-mid flex-shrink-0" size={13} />
            <p className="text-xs text-neutral-secondary-mid">
              Leeg sinds: <span className="text-neutral-primary">{formattedDate}</span>
              {days !== null && days > 0 && (
                <span className={`ml-1 font-medium ${days > 60 ? 'text-red-500' : days > 30 ? 'text-amber-600' : 'text-neutral-secondary-mid'}`}>
                  ({days}d)
                </span>
              )}
            </p>
          </div>
        )}

        {unit.advertisedRentalPrice && (
          <div className="flex items-center gap-1.5">
            <CurrencyEur className="text-neutral-secondary-mid flex-shrink-0" size={13} />
            <p className="text-xs text-neutral-secondary-mid">
              Huurprijs: <span className="text-neutral-primary font-medium">€{unit.advertisedRentalPrice}/mnd</span>
            </p>
          </div>
        )}

        <div className="flex items-center pt-1 border-t border-border">
          <FlowIndicator activeStep={3} />
        </div>
      </CardContent>
    </Card>
  );
};
